<template>
  <transition name="drawer-slide">
    <div v-if="drawerVisible" class="drawer-mask" @click.self="handleClose">
      <div class="drawer-panel" :style="drawerPanelStyle">
        <div class="drawer-header">
          <span class="drawer-title">{{ drawerConfig.title || $t('workbench.panel') }}</span>
          <i class="el-icon-close drawer-close" @click="handleClose"></i>
        </div>
        <div class="drawer-body">
          <!-- form type (dynamic) -->
          <template v-if="drawerType === 'form'">
            <div v-if="apiLoading" class="drawer-loading">
              <i class="el-icon-loading"></i>
              <span>{{ $t('workbench.loadingFormConfig') }}</span>
            </div>
            <div v-else-if="parsedFormConfig.length === 0" class="drawer-empty">{{ $t('workbench.noFormConfig') }}</div>
            <div v-else class="drawer-form">
              <template v-for="field in parsedFormConfig">
                <!-- divider -->
                <div v-if="field.type === 'divider'" :key="field.key" class="form-divider">
                  {{ field.label }}
                </div>
                <div v-else :key="field.key" class="form-field">
                  <label class="field-label">
                    {{ field.label || field.key }}
                    <span v-if="field.required" class="field-required">*</span>
                  </label>
                  <!-- input / password -->
                  <input
                    v-if="field.type === 'input' || field.type === 'text' || field.type === 'string' || !field.type"
                    class="field-input"
                    :type="field.password ? 'password' : 'text'"
                    :value="formData[field.key] != null ? formData[field.key] : ''"
                    :placeholder="field.placeholder || $t('workbench.pleaseInput')"
                    :disabled="!!selectedValue || !!field.disabled"
                    @input="handleFieldInput(field.key, $event)"
                  />
                  <!-- number / inputNumber -->
                  <el-input-number
                    v-else-if="field.type === 'number' || field.type === 'inputNumber'"
                    :value="formData[field.key]"
                    :min="field.min"
                    :max="field.max"
                    :step="field.step || 1"
                    :placeholder="field.placeholder || $t('workbench.pleaseInput')"
                    :disabled="!!selectedValue || !!field.disabled"
                    controls-position="right"
                    class="field-number"
                    @change="handleFieldChange(field.key, $event)"
                  />
                  <!-- textarea -->
                  <textarea
                    v-else-if="field.type === 'textarea'"
                    class="field-textarea"
                    :value="formData[field.key] || ''"
                    :placeholder="field.placeholder || $t('workbench.pleaseInput')"
                    :rows="field.rows || 3"
                    :disabled="!!selectedValue || !!field.disabled"
                    @input="handleFieldInput(field.key, $event)"
                  ></textarea>
                  <!-- select -->
                  <el-select
                    v-else-if="field.type === 'select' || field.type === 'dropdown'"
                    :value="formData[field.key] != null && typeof formData[field.key] === 'object' ? formData[field.key].value : formData[field.key]"
                    :placeholder="field.placeholder || (fieldLoading[field.key] ? $t('workbench.loading') : $t('workbench.pleaseSelect'))"
                    :multiple="!!field.multiple"
                    class="field-select"
                    :disabled="!!selectedValue || !!field.disabled || fieldLoading[field.key]"
                    :loading="!!fieldLoading[field.key]"
                    @change="handleSelectChange(field, $event)"
                  >
                    <el-option
                      v-for="(opt, oidx) in (fieldOptions[field.key] || field.options || [])"
                      :key="oidx"
                      :label="opt.label || opt"
                      :value="opt.value !== undefined ? opt.value : opt"
                    />
                  </el-select>
                  <!-- cascader -->
                  <el-cascader
                    v-else-if="field.type === 'cascader'"
                    :value="formData[field.key]"
                    :options="fieldOptions[field.key] || field.options || []"
                    :props="{ multiple: !!field.multiple, checkStrictly: !!field.changeOnSelect, expandTrigger: field.expandTrigger || 'click' }"
                    :placeholder="field.placeholder || $t('workbench.pleaseSelect')"
                    class="field-cascader"
                    :disabled="!!selectedValue || !!field.disabled"
                    clearable
                    @change="handleFieldChange(field.key, $event)"
                  />
                  <!-- switch -->
                  <el-switch
                    v-else-if="field.type === 'switch'"
                    :value="formData[field.key]"
                    :disabled="!!selectedValue || !!field.disabled"
                    @change="handleFieldChange(field.key, $event)"
                  />
                  <!-- date -->
                  <el-date-picker
                    v-else-if="field.type === 'date'"
                    :value="formData[field.key]"
                    type="date"
                    :placeholder="field.placeholder || $t('workbench.pleaseSelectDate')"
                    value-format="yyyy-MM-dd"
                    class="field-datepicker"
                    :disabled="!!selectedValue || !!field.disabled"
                    @input="handleFieldChange(field.key, $event)"
                  />
                  <!-- dateRange -->
                  <el-date-picker
                    v-else-if="field.type === 'dateRange'"
                    :value="formData[field.key]"
                    type="daterange"
                    :range-separator="$t('workbench.to')"
                    :start-placeholder="$t('workbench.startDate')"
                    :end-placeholder="$t('workbench.endDate')"
                    value-format="yyyy-MM-dd"
                    class="field-datepicker"
                    :disabled="!!selectedValue || !!field.disabled"
                    @input="handleFieldChange(field.key, $event)"
                  />
                  <!-- radio -->
                  <el-radio-group
                    v-else-if="field.type === 'radio' || field.type === 'radioGroup'"
                    :value="formData[field.key]"
                    :disabled="!!selectedValue || !!field.disabled"
                    @input="handleFieldChange(field.key, $event)"
                  >
                    <el-radio v-for="(opt, oidx) in (fieldOptions[field.key] || field.options || [])" :key="oidx" :label="opt.value !== undefined ? opt.value : opt">
                      {{ opt.label || opt }}
                    </el-radio>
                  </el-radio-group>
                  <!-- checkbox / checkboxGroup -->
                  <el-checkbox-group
                    v-else-if="field.type === 'checkbox' || field.type === 'checkboxGroup'"
                    :value="formData[field.key] || []"
                    :disabled="!!selectedValue || !!field.disabled"
                    @input="handleFieldChange(field.key, $event)"
                  >
                    <el-checkbox v-for="(opt, oidx) in (fieldOptions[field.key] || field.options || [])" :key="oidx" :label="opt.value !== undefined ? opt.value : opt">
                      {{ opt.label || opt }}
                    </el-checkbox>
                  </el-checkbox-group>
                  <!-- slider -->
                  <el-slider
                    v-else-if="field.type === 'slider'"
                    :value="formData[field.key] || field.min || 0"
                    :min="field.min || 0"
                    :max="field.max || 100"
                    :step="field.step || 1"
                    :disabled="!!selectedValue || !!field.disabled"
                    @change="handleFieldChange(field.key, $event)"
                  />
                  <!-- upload (单文件上传) -->
                  <div v-else-if="field.type === 'upload'" class="field-upload">
                    <ele-upload-file
                      :value="formData[field.key]"
                      :fileSize="field.fileSize || 10"
                      :limit="1"
                      :action="getUploadAction(field)"
                      :data="getUploadData(field)"
                      :headers="uploadHeaders"
                      :accept="field.accept || ''"
                      :fileType="field.fileType || []"
                      :placeholder="field.placeholder || $t('workbench.uploadFile')"
                      :disabled="!!selectedValue || !!field.disabled"
                      :response-fn="(res) => uploadResponseFn(res, field)"
                      :returnDataFormat="'string'"
                      @input="(val) => handleFieldChange(field.key, val)"
                    />
                  </div>
                  <!-- imageUpload (多图片上传) -->
                  <div v-else-if="field.type === 'imageUpload'" class="field-image-upload">
                    <ele-upload-image
                      :value="formData[field.key] || []"
                      :fileSize="field.fileSize || 3"
                      :size="field.size || 80"
                      :limit="field.limit || 6"
                      :action="getUploadAction(field)"
                      :data="getUploadData(field)"
                      :headers="uploadHeaders"
                      :accept="field.accept || '.png,.jpg,.jpeg'"
                      :fileType="field.fileType || []"
                      :disabled="!!selectedValue || !!field.disabled"
                      :response-fn="(res) => imageUploadResponseFn(res, field)"
                      :multiple="field.multiple !== false"
                      :position="field.position || 'left'"
                      @input="(val) => handleFieldChange(field.key, val)"
                    />
                  </div>
                  <!-- fileUpload (多文件上传) -->
                  <div v-else-if="field.type === 'fileUpload'" class="field-file-upload">
                    <ele-upload-file
                      :value="formData[field.key] || []"
                      :fileSize="field.fileSize || 10"
                      :limit="field.limit || 10"
                      :action="getUploadAction(field)"
                      :data="getUploadData(field)"
                      :headers="uploadHeaders"
                      :isCanDelete="true"
                      :accept="field.accept || ''"
                      :fileType="field.fileType || []"
                      :placeholder="field.placeholder || $t('workbench.uploadFiles')"
                      :disabled="!!selectedValue || !!field.disabled"
                      :response-fn="(res) => uploadResponseFn(res, field)"
                      :multiple="field.multiple !== false"
                      @input="(val) => handleFieldChange(field.key, val)"
                    />
                  </div>
                  <!-- fallback input -->
                  <input
                    v-else
                    class="field-input"
                    :value="formData[field.key] != null ? formData[field.key] : ''"
                    :placeholder="field.placeholder || $t('workbench.pleaseInput')"
                    :disabled="!!selectedValue || !!field.disabled"
                    @input="handleFieldInput(field.key, $event)"
                  />
                </div>
              </template>
              <div class="drawer-footer">
                <button class="drawer-btn-primary" :disabled="!!selectedValue" @click="handleFormSubmit">
                  {{ drawerConfig.btnText || $t('workbench.submit') }}
                </button>
              </div>
            </div>
          </template>

          <!-- skillCard type -->
          <template v-else-if="drawerType === 'skillCard'">
            <div class="drawer-skill-card">
              <div class="skill-info">
                <div class="skill-name">{{ drawerConfig.skillTitle || $t('workbench.skill') }}</div>
                <div class="skill-desc">{{ drawerConfig.skillDesc || drawerConfig.description || '' }}</div>
              </div>
              <div class="drawer-footer">
                <button class="drawer-btn-primary" :disabled="!!selectedValue" @click="handleSkillConfirm">
                  {{ drawerConfig.btnText || $t('workbench.installSkill') }}
                </button>
              </div>
            </div>
          </template>

          <!-- cardList type -->
          <template v-else-if="drawerType === 'cardList'">
            <div class="drawer-cardlist-content">
              <div
                v-for="(item, idx) in parsedCardListData"
                :key="idx"
                class="drawer-cardlist-item"
                :class="{ selected: cardListSelectedIdx === idx, 'display-only': !!selectedValue || drawerConfig.displayOnly }"
                @click="selectedValue ? null : selectCardItem(idx)"
              >
                <img v-if="getCardItemImage(item)" :src="getCardItemImage(item)" class="cardlist-img" />
                <div class="cardlist-info">
                  <div class="cardlist-name">{{ getCardItemName(item) }}</div>
                  <div class="cardlist-desc">{{ getCardItemDesc(item) }}</div>
                </div>
                <input
                  v-if="drawerConfig.multiple"
                  type="checkbox"
                  class="cardlist-checkbox"
                  :checked="cardListSelectedIdx === idx || (multipleSelected.includes(idx))"
                  :disabled="!!selectedValue"
                  @click.stop="selectedValue ? null : toggleMultipleSelect(idx)"
                />
                <input
                  v-else
                  type="radio"
                  class="cardlist-radio"
                  :name="drawerScopeKey + '-cardlist'"
                  :checked="cardListSelectedIdx === idx"
                  :disabled="!!selectedValue"
                  @click.stop="selectedValue ? null : (cardListSelectedIdx = idx)"
                />
              </div>
            </div>
            <div class="drawer-footer">
              <button class="drawer-btn-cancel" @click="handleClose">{{ $t('workbench.cancel') }}</button>
              <button
                class="drawer-btn-primary"
                :disabled="!!selectedValue || (drawerConfig.multiple ? multipleSelected.length === 0 : cardListSelectedIdx === null)"
                @click="handleCardListConfirm"
              >
                {{ drawerConfig.btnText || $t('workbench.confirm') }}
              </button>
            </div>
          </template>

          <!-- tableList type -->
          <template v-else-if="drawerType === 'tableList'">
            <div class="card-table-wrapper">
              <table class="card-table">
                <thead>
                  <tr>
                    <th class="radio-th"></th>
                    <th v-for="(col, idx) in parsedColumns" :key="idx">{{ col.title || col }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in parsedTableData" :key="idx"
                      :class="{ selected: tableSelectedIdx === idx, 'display-only': !!selectedValue }"
                      @click="selectedValue ? null : (tableSelectedIdx = idx)">
                    <td class="radio-td">
                      <input type="radio" :name="drawerScopeKey + '-drawer-table'" :checked="tableSelectedIdx === idx" :disabled="!!selectedValue" @click.stop="selectedValue ? null : (tableSelectedIdx = idx)" />
                    </td>
                    <td v-for="(col, cidx) in parsedColumns" :key="cidx">{{ row[col.dataIndex || col.key || cidx] }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="drawer-footer">
                <button class="drawer-btn-cancel" @click="handleClose">{{ $t('workbench.cancel') }}</button>
                <button class="drawer-btn-primary" :disabled="!!selectedValue || tableSelectedIdx === null" @click="handleTableConfirm">
                  {{ drawerConfig.confirmText || $t('workbench.confirm') }}
                </button>
              </div>
            </div>
          </template>

          <!-- iframe type -->
          <template v-else-if="drawerType === 'iframe'">
            <div v-if="iframeLoading" class="drawer-loading">
              <i class="el-icon-loading"></i>
              <span>{{ drawerConfig.loading || $t('workbench.loading') }}</span>
            </div>
            <iframe
              v-show="!iframeLoading"
              v-if="drawerConfig.url"
              ref="drawerIframe"
              :src="drawerConfig.url"
              class="drawer-iframe"
              :style="{ width: drawerConfig.width || '100%', height: drawerConfig.height || '100%' }"
              frameborder="0"
              @load="onIframeLoad($event.target)"
            ></iframe>
          </template>

          <!-- fallback -->
          <template v-else>
            <div class="drawer-empty">{{ $t('workbench.unknownPanelType', { type: drawerType }) }}</div>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import axios from 'axios'
import { getToken } from '@/utils/auth'
const adminUrl = ''
export default {
  name: 'CosmoClawDrawer',
  data() {
    return {
      formData: {},
      tableSelectedIdx: null,
      cardListSelectedIdx: null,
      multipleSelected: [],
      apiFormConfig: [],
      apiLoading: false,
      iframeLoading: true,
      fieldOptions: {},
      fieldLoading: {}
    }
  },
  computed: {
    drawerVisible() {
      return this.$store.state.cosmoClaw.drawerVisible
    },
    drawerConfig() {
      return this.$store.state.cosmoClaw.drawerConfig || {}
    },
    drawerType() {
      const t = this.drawerConfig.type || 'form'
      if (['skillCard', 'tableList', 'cardList', 'iframe'].includes(t)) return t
      return 'form'
    },
    drawerPanelStyle() {
      const w = this.drawerConfig.drawerWidth
      return w ? { width: w } : {}
    },
    drawerScopeKey() {
      const action = this.drawerConfig.action
      const cardScopeKey = this.drawerConfig.cardScopeKey
      return action ? `${cardScopeKey || 'global'}::${action}` : null
    },
    selectedValue() {
      if (this.drawerScopeKey) {
        const fromStore = this.$store.state.cosmoClaw.cardSelections[this.drawerScopeKey]
        if (fromStore !== undefined) return fromStore
      }
      // 从历史消息中恢复已提交的状态
      const sv = this.submittedValue
      if (sv !== null && sv !== undefined) return sv
      // tableList 使用 [table-action] 提交，submittedValue 查不到，需查 submittedDatasetKey
      if (this.drawerType === 'tableList' || this.drawerType === 'cardList') {
        const dk = this.submittedDatasetKey
        if (dk !== null && dk !== undefined) return dk
      }
      return null
    },
    submittedValue() {
      if (!this.drawerScopeKey || !this.drawerConfig.action) return null
      const action = this.drawerConfig.action
      const messages = this.$store.state.cosmoClaw.messages || []
      // 找到当前 drawer 所在消息的索引，只检查该消息之后的提交
      let startIdx = -1
      const msgId = this.drawerConfig.messageId
      if (msgId) {
        for (let i = 0; i < messages.length; i++) {
          if (messages[i].id === msgId) { startIdx = i; break }
        }
        if (startIdx === -1) return null
      } else {
        startIdx = 0
      }
      for (let i = messages.length - 1; i >= startIdx; i--) {
        const msg = messages[i]
        if (msg.type !== 'user') continue
        const content = (msg.content || '').trim()
        if (content.startsWith('[form-action]') || content.startsWith('[dataset-action]')) {
          const prefix = content.startsWith('[form-action]') ? '[form-action]' : '[dataset-action]'
          const jsonStr = content.slice(prefix.length)
          // 对照前一条 assistant 消息确认是同一个 action
          for (let j = i - 1; j >= 0; j--) {
            const assistantMsg = messages[j]
            if (assistantMsg.type === 'assistant') {
              if (assistantMsg.content.includes(`"${action}"`)) {
                return jsonStr
              }
              break
            }
          }
        }
      }
      return null
    },
    submittedDatasetKey() {
      if (!this.drawerScopeKey || !this.drawerConfig.action) return null
      const action = this.drawerConfig.action
      const messages = this.$store.state.cosmoClaw.messages || []
      let startIdx = -1
      const msgId = this.drawerConfig.messageId
      if (msgId) {
        for (let i = 0; i < messages.length; i++) {
          if (messages[i].id === msgId) { startIdx = i; break }
        }
        if (startIdx === -1) return null
      } else {
        startIdx = 0
      }
      for (let i = messages.length - 1; i >= startIdx; i--) {
        const msg = messages[i]
        if (msg.type !== 'user') continue
        const content = (msg.content || '').trim()
        // cardList 使用 [dataset-action]，tableList 使用 [table-action]
        if (content.startsWith('[dataset-action]') || content.startsWith('[table-action]')) {
          const prefix = content.startsWith('[dataset-action]') ? '[dataset-action]' : '[table-action]'
          const value = content.slice(prefix.length)
          for (let j = i - 1; j >= 0; j--) {
            const assistantMsg = messages[j]
            if (assistantMsg.type === 'assistant') {
              if (assistantMsg.content.includes(`"${action}"`)) {
                return value
              }
              break
            }
          }
        }
      }
      return null
    },
    parsedFormConfig() {
      // API 动态加载的表单配置优先
      if (this.apiFormConfig.length > 0) return this.apiFormConfig
      const raw = this.drawerConfig.formConfig
      if (!raw) return []
      try {
        const parsed = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed : []
      } catch { return [] }
    },
    uploadHeaders() {
      const token = this.$store.getters.token || ''
      return { Authorization: `Bearer ${token}` }
    },
    parsedTableData() {
      const raw = this.drawerConfig.tableData
      if (!raw) return []
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) } catch { return [] }
      }
      return []
    },
    parsedColumns() {
      const raw = this.drawerConfig.columns
      if (!raw) return []
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) } catch { return [] }
      }
      return []
    },
    parsedCardListData() {
      const raw = this.drawerConfig.cardListData
      if (!raw) return []
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) } catch { return [] }
      }
      return []
    },
    cardListAttribute() {
      const raw = this.drawerConfig.attribute
      if (!raw) return { name: 'name', desc: 'desc', image: 'image' }
      if (typeof raw === 'object') return raw
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) } catch { return { name: 'name', desc: 'desc', image: 'image' } }
      }
      return { name: 'name', desc: 'desc', image: 'image' }
    }
  },
  watch: {
    drawerVisible(val) {
      if (val) {
        document.body.style.overflow = 'hidden'
        this.initDrawer()
      } else {
        document.body.style.overflow = ''
        // 关闭时重置 cardList 选中状态
        this.cardListSelectedIdx = null
        this.multipleSelected = []
      }
    },
    drawerConfig: {
      handler() {
        // 抽屉已打开时 config 变化 → 重新初始化面板（流式多段更新 / 历史切换）
        if (this.drawerVisible) {
          this.$nextTick(() => this.initDrawer())
        }
      }
    }
  },
  beforeDestroy() {
    document.body.style.overflow = ''
  },
  methods: {
    onIframeLoad(iframe) {
      this.iframeLoading = false
      // // const iframe = this.$refs.drawerIframe
      // if (iframe && iframe.contentWindow) {
      //   try {
      //     const token = getToken()
      //     debugger
      //     iframe.contentWindow.postMessage(
      //       JSON.stringify({
      //         action: 'token_send',
      //         payload: {
      //           token,
      //           expireAt: Date.now()
      //         },
      //         timestamp: Date.now()
      //       }),
      //       '*'
      //     )
      //   } catch (e) {
      //     console.warn('[CosmoClawDrawer] postMessage 失败:', e)
      //   }
      // }
      this.$nextTick(() => {
        console.log('[  ]-403---------------------------')
        const token = getToken()
        if (!token) return
        console.log('[ token ]-404', token)
        iframe.contentWindow.postMessage(
          JSON.stringify({
            action: 'token_send',
            payload: {
              token,
              expireAt: Date.now()
            },
            timestamp: Date.now()
          }),
          '*'
        )
      })
    },
    initDrawer() {
      this.formData = {}
      this.tableSelectedIdx = null
      this.apiFormConfig = []
      this.iframeLoading = true

      // 如果是 API 类型表单，先加载表单配置
      if (this.drawerType === 'form' && this.drawerConfig.formType === 'api' && this.drawerConfig.formApi) {
        this.loadFormApi()
      } else if (this.drawerType === 'form') {
        // 静态表单配置，初始化级联字段
        this.$nextTick(() => this._initCascadeFields())
      }

      // 优先从 cardSelections 恢复，否则从历史消息恢复
      let val = null
      if (this.drawerScopeKey) {
        val = this.$store.state.cosmoClaw.cardSelections[this.drawerScopeKey] ?? null
      }
      if (val === null) val = this.submittedValue
      if (val === null && this.drawerType === 'tableList') val = this.submittedDatasetKey
      if (val === null || val === undefined) return

      if (this.drawerType === 'form') {
        try {
          const parsed = JSON.parse(val)
          if (typeof parsed === 'object' && parsed !== null) {
            if (Array.isArray(parsed)) {
              const converted = {}
              parsed.forEach(item => { if (item.key != null) converted[item.key] = item.value })
              this.formData = converted
            } else {
              this.formData = parsed
            }
          }
        } catch { /* ignore */ }
      } else if (this.drawerType === 'tableList') {
        const selectKey = this.drawerConfig.select_key || this.drawerConfig.selectKey || 'id'
        const data = this.parsedTableData
        const matchedIdx = data.findIndex(row => String(row[selectKey]) === String(val))
        this.tableSelectedIdx = matchedIdx >= 0 ? matchedIdx : null
      } else if (this.drawerType === 'cardList') {
        const list = this.parsedCardListData
        if (list.length > 0) {
          const selectedKey = this.drawerConfig.selectedKey || 'id'
          if (this.drawerConfig.multiple) {
            const ids = typeof val === 'string' ? val.split(',') : (Array.isArray(val) ? val : [])
            this.multipleSelected = ids
          } else {
            this.cardListSelectedIdx = list.findIndex(item => String(item[selectedKey]) === String(val))
          }
        }
      }
    },
    async loadFormApi() {
      // 按照 React 项目逻辑：先解析 formApi 字符串
      let formApiConfig = this.drawerConfig.formApi
      if (typeof formApiConfig === 'string') {
        try { formApiConfig = JSON.parse(formApiConfig) } catch { return }
      }
      if (!formApiConfig || !formApiConfig.url) return

      const { url: rawUrl, method = 'GET', params } = formApiConfig
      // URL 处理：绝对路径直接用，相对路径加 /dynamic-api 前缀
      const url = rawUrl
      this.apiLoading = true
      try {
        let res
        if (method.toUpperCase() === 'GET') {
          res = await axios.get(url, { params })
        } else {
          res = await axios.post(url, params)
        }
        const result = res.data
        // 按照 React 逻辑解析响应
        if (Array.isArray(result)) {
          this.apiFormConfig = result
        } else if (result && result.formConfig) {
          const fc = result.formConfig
          this.apiFormConfig = typeof fc === 'string' ? JSON.parse(fc) : fc
        } else {
          this.apiFormConfig = []
        }
        // 设置默认值
        this.apiFormConfig.forEach(field => {
          if (field.defaultValue !== undefined && this.formData[field.key] === undefined) {
            this.$set(this.formData, field.key, field.defaultValue)
          }
        })
        // 初始化级联字段
        this.$nextTick(() => this._initCascadeFields())
      } catch (e) {
        console.error('[CosmoClawDrawer] loadFormApi error:', e)
        this.apiFormConfig = []
      } finally {
        this.apiLoading = false
      }
    },
    handleFieldChange(key, value) {
      this.$set(this.formData, key, value)
      this._triggerDependentFields(key)
    },
    handleSelectChange(field, value) {
      const options = this.fieldOptions[field.key] || field.options || []
      const matched = options.find(opt => {
        const optVal = opt.value !== undefined ? opt.value : opt
        return optVal === value
      })
      this.$set(this.formData, field.key, matched || value)
      this._triggerDependentFields(field.key)
    },
    handleFieldInput(key, event) {
      this.$set(this.formData, key, event.target.value)
    },
    _initCascadeFields() {
      // 加载所有顶层级联字段（有 api 无 deps 的字段）
      this.parsedFormConfig.forEach(field => {
        if (field.api && (!field.deps || !Array.isArray(field.deps) || field.deps.length === 0)) {
          this.loadFieldOptions(field)
        }
      })
    },
    _triggerDependentFields(changedKey) {
      this.parsedFormConfig.forEach(field => {
        if (field.api && field.deps && Array.isArray(field.deps) && field.deps.includes(changedKey)) {
          this.$set(this.formData, field.key, field.multiple ? [] : '')
          const allDepsFilled = field.deps.every(dep => {
            const val = this.formData[dep]
            return val != null && val !== '' && !(Array.isArray(val) && val.length === 0)
          })
          if (allDepsFilled) {
            this.loadFieldOptions(field)
          } else {
            this.$set(this.fieldOptions, field.key, [])
          }
        }
      })
    },
    async loadFieldOptions(field) {
      let apiConfig = field.api
      if (typeof apiConfig === 'string') {
        apiConfig = { url: apiConfig, method: 'GET' }
      }
      if (!apiConfig || !apiConfig.url) return

      let url = apiConfig.url
      if (field.deps && Array.isArray(field.deps)) {
        field.deps.forEach(depKey => {
          url = url.replace(new RegExp(`\\{${depKey}\\}`, 'g'), this.formData[depKey] || '')
        })
      }
      const fullUrl = url.startsWith('http') ? url : `/dynamic-api${url.startsWith('/') ? '' : '/'}${url}`
      const method = (apiConfig.method || 'GET').toUpperCase()

      this.$set(this.fieldLoading, field.key, true)
      try {
        let res
        if (method === 'GET') {
          res = await axios.get(fullUrl, { params: apiConfig.params })
        } else {
          res = await axios.post(fullUrl, apiConfig.params)
        }
        const data = Array.isArray(res.data) ? res.data
          : (res.data.options || res.data.data || res.data.list || [])
        this.$set(this.fieldOptions, field.key, data)
      } catch (e) {
        console.error(`[CosmoClawDrawer] loadFieldOptions ${field.key} error:`, e)
        this.$set(this.fieldOptions, field.key, [])
      } finally {
        this.$set(this.fieldLoading, field.key, false)
      }
    },
    // 上传相关方法
    getUploadAction(field) {
      if (field.action) return field.action
      return adminUrl + 'file'
    },
    getUploadData(field) {
      return field.data || { bucket: field.bucket || 'onecosmodev' }
    },
    uploadResponseFn(response, file) {
      // 处理上传响应，返回包含 url 的对象
      const result = {
        src: response.result.path,
        url: response.result.path,
        title: response.result.object,
        ...response.result
      }

      return result
    },
    imageUploadResponseFn(response, file) {
      // 处理图片上传响应，返回包含 src 的对象
      const result = response.result || response
      return {
        src: result.path || result.url || '',
        title: file ? file.name : '',
        ...result
      }
    },
    handleFormSubmit() {
      if (this.selectedValue) return
      const action = this.drawerConfig.action || ''
      // React 格式：[{key, type, value}] 数组，select 类型提交完整 option 对象
      const formDataList = this.parsedFormConfig
        .filter(f => f.type !== 'divider')
        .map(field => {
          const raw = this.formData[field.key] ?? field.defaultValue ?? ''
          return { key: field.key, type: field.type, value: raw }
        })
      const jsonStr = JSON.stringify(formDataList)
      const message = `[form-action]${jsonStr}`
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action, value: jsonStr, message }
      }))
      if (this.drawerScopeKey) {
        this.$store.commit('cosmoClaw/SET_CARD_SELECTION', {
          key: this.drawerScopeKey,
          value: jsonStr
        })
      }
      this.handleClose()
    },
    handleSkillConfirm() {
      if (this.selectedValue) return
      const action = this.drawerConfig.action || ''
      const btnText = this.drawerConfig.btnText || this.$t('workbench.confirm')
      const message = `[skill-action]${btnText}`
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action, value: 'confirm', message }
      }))
      if (this.drawerScopeKey) {
        this.$store.commit('cosmoClaw/SET_CARD_SELECTION', {
          key: this.drawerScopeKey,
          value: btnText
        })
      }
      this.handleClose()
    },
    handleTableConfirm() {
      if (this.selectedValue || this.tableSelectedIdx === null) return
      // 使用 parsedTableData 确保 JSON 字符串已被正确解析为数组
      const tableData = this.parsedTableData
      // 兼容 select_key（下划线）和 selectKey（驼峰）两种格式
      const selectKey = this.drawerConfig.select_key || this.drawerConfig.selectKey
      const row = tableData && tableData.length > 0 ? tableData[this.tableSelectedIdx] : null
      if (row && selectKey && row[selectKey] !== undefined) {
        const value = String(row[selectKey])
        const action = this.drawerConfig.action || ''
        const message = `[table-action]${value}`
        document.dispatchEvent(new CustomEvent('md-card-action', {
          detail: { action, value, message }
        }))
        if (this.drawerScopeKey) {
          this.$store.commit('cosmoClaw/SET_CARD_SELECTION', {
            key: this.drawerScopeKey,
            value
          })
        }
      }
      this.handleClose()
    },
    // cardList 方法
    getCardItemName(item) {
      const attr = this.cardListAttribute
      const field = attr.name || 'name'
      return item[field] || ''
    },
    getCardItemDesc(item) {
      const attr = this.cardListAttribute
      const field = attr.desc || 'desc'
      return item[field] || ''
    },
    getCardItemImage(item) {
      const attr = this.cardListAttribute
      const field = attr.image || 'image'
      return item[field] || ''
    },
    selectCardItem(idx) {
      if (this.selectedValue || this.drawerConfig.displayOnly) return
      if (this.drawerConfig.multiple) {
        this.toggleMultipleSelect(idx)
      } else {
        this.cardListSelectedIdx = idx
      }
    },
    toggleMultipleSelect(idx) {
      const i = this.multipleSelected.indexOf(idx)
      if (i >= 0) {
        this.multipleSelected.splice(i, 1)
      } else {
        this.multipleSelected.push(idx)
      }
    },
    handleCardListConfirm() {
      if (this.selectedValue) return
      const list = this.parsedCardListData
      const selectedKey = this.drawerConfig.selectedKey || 'id'
      let value = ''
      if (this.drawerConfig.multiple) {
        value = this.multipleSelected.map(idx => list[idx]?.[selectedKey]).filter(v => v != null).join(',')
      } else {
        if (this.cardListSelectedIdx === null) return
        value = String(list[this.cardListSelectedIdx]?.[selectedKey] || '')
      }
      const action = this.drawerConfig.action || ''
      const message = `[dataset-action]${value}`
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action, value, message }
      }))
      if (this.drawerScopeKey) {
        this.$store.commit('cosmoClaw/SET_CARD_SELECTION', {
          key: this.drawerScopeKey,
          value
        })
      }
      this.handleClose()
    },
    handleClose() {
      this.$store.commit('cosmoClaw/SET_DRAWER_VISIBLE', false)
    }
  }
}
</script>

<style lang="scss" scoped>
.drawer-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}

.drawer-panel {
  width: 480px;
  max-width: 90vw;
  height: 100vh;
  background: #fff;
  border-left: 1px solid #e8e8e8;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.drawer-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}

.drawer-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.drawer-close {
  font-size: 16px;
  color: #999;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  &:hover { background: #f5f5f5; }
}

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.drawer-empty {
  text-align: center;
  color: #999;
  padding: 40px 0;
}

.drawer-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 14px;
  color: #1f2329;
  font-weight: 500;
}

.field-required { color: #f5222d; }

.field-input, .field-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  &:focus { border-color: #3370ff; }
  &:disabled { background: #f5f5f5; color: #999; cursor: not-allowed; }
}

.field-textarea { resize: vertical; }

.field-select { width: 100%; }

.field-datepicker { width: 100%; }

.field-upload,
.field-image-upload,
.field-file-upload {
  width: 100%;
}

.field-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
  input { cursor: pointer; }
}

.drawer-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 16px 20px;
  margin-top: 16px;
}

.drawer-btn-primary {
  padding: 8px 24px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  background: linear-gradient(270deg, #4A5FF3 0%, #4E86FF 100%);
  color: #fff;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
  &:disabled { background: #d9d9d9; cursor: not-allowed; }
}

.drawer-btn-cancel {
  padding: 8px 24px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: 1px solid #d9d9d9;
  background: #fff;
  color: #1f2329;
  &:hover { border-color: #3370ff; }
}

.drawer-skill-card {
  padding: 20px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.skill-name { font-size: 16px; font-weight: 600; margin-bottom: 8px; }
.skill-desc { font-size: 13px; color: #666; line-height: 1.6; }

.card-table-wrapper { overflow-x: auto; margin-bottom: 16px; }

.card-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 400px;
  th, td { padding: 8px 12px; text-align: center; border-bottom: 1px solid #f0f0f0; }
  th { background: #fafafa; font-weight: 600; color: #333; }
  tr { cursor: pointer; }
  tr:hover { background: #f5f7fa; }
  tr.selected { background: #e8f4ff; }
  tr.display-only {
    cursor: default;
    opacity: 0.85;
    pointer-events: none;
  }
}

.radio-th { width: 40px; }
.radio-td {
  width: 40px;
  input[type="radio"] { cursor: pointer; width: 15px; height: 15px; accent-color: #3370ff; }
}

.drawer-iframe {
  width: 100%;
  height: 100%;
  min-height: 400px;
  border: none;
  border-radius: 8px;
}

.drawer-cardlist-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.drawer-cardlist-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #3370ff; box-shadow: 0 2px 8px rgba(79, 110, 255, 0.12); }
  &.selected { border-color: #3370ff; background: #f0f5ff; }
  &.display-only {
    cursor: default;
    opacity: 0.85;
    background: #fafafa;
    pointer-events: none;
    border-color: #e8e8e8;
  }
}

.cardlist-img {
  width: 48px;
  height: 48px;
  border-radius: 6px;
  object-fit: cover;
  flex-shrink: 0;
  background: #f5f5f5;
}

.cardlist-info {
  flex: 1;
  min-width: 0;
}

.cardlist-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2329;
  line-height: 1.4;
}

.cardlist-desc {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cardlist-radio,
.cardlist-checkbox {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  accent-color: #3370ff;
  cursor: pointer;
}

.drawer-slide-enter-active, .drawer-slide-leave-active {
  transition: opacity 0.3s;
  .drawer-panel { transition: transform 0.3s ease; }
}
.drawer-slide-enter, .drawer-slide-leave-to {
  opacity: 0;
  .drawer-panel { transform: translateX(100%); }
}
</style>
